import {
  ImportJobConflictError,
  ImportProviderFileConsumptionsResult,
  ImportStartResponse,
  ProviderImportJob,
  ProviderImportJobStatus,
} from '../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { ProviderConsumptionOrigin } from '../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { StatusTone } from '../theme/guideTokens';

/**
 * Importación de consumos en segundo plano (spec 008, MedicalProtocols 030): lógica pura del job
 * (estado, progreso, vínculos del aviso) y los accesos a sessionStorage / localStorage / Notification API,
 * todos con try/catch (pueden fallar en ventanas privadas o con el almacenamiento bloqueado).
 */

/** Consulta del paso 3 del asistente (GET import-jobs/{id}). */
export const IMPORT_JOB_DRAWER_POLL_MS = 3000;
/** Consulta del aviso global (GET import-jobs/mine), sólo mientras haya jobs activos. */
export const IMPORT_JOBS_WATCH_POLL_MS = 5000;

const STATUS_BY_NAME: Record<string, ProviderImportJobStatus> = {
  queued: ProviderImportJobStatus.Queued,
  running: ProviderImportJobStatus.Running,
  succeeded: ProviderImportJobStatus.Succeeded,
  failed: ProviderImportJobStatus.Failed,
};

/** Estado normalizado (número 1..4 o nombre); undefined si no se reconoce. */
export const importJobStatusOf = (job: Pick<ProviderImportJob, 'status'> | null | undefined): ProviderImportJobStatus | undefined => {
  const raw = job?.status;
  if (raw === null || raw === undefined) return undefined;
  const numeric = Number(raw);
  if (Number.isInteger(numeric) && numeric >= ProviderImportJobStatus.Queued && numeric <= ProviderImportJobStatus.Failed) return numeric;
  return STATUS_BY_NAME[String(raw).trim().toLowerCase()];
};

/** En cola o en curso. */
export const isImportJobActive = (job: Pick<ProviderImportJob, 'status'> | null | undefined): boolean => {
  const status = importJobStatusOf(job);
  return status === ProviderImportJobStatus.Queued || status === ProviderImportJobStatus.Running;
};

export const isImportJobSucceeded = (job: Pick<ProviderImportJob, 'status'> | null | undefined): boolean =>
  importJobStatusOf(job) === ProviderImportJobStatus.Succeeded;

export const isImportJobFailed = (job: Pick<ProviderImportJob, 'status'> | null | undefined): boolean =>
  importJobStatusOf(job) === ProviderImportJobStatus.Failed;

/** Terminó (bien o mal). */
export const isImportJobFinished = (job: Pick<ProviderImportJob, 'status'> | null | undefined): boolean =>
  isImportJobSucceeded(job) || isImportJobFailed(job);

const STATUS_KEYS: Record<ProviderImportJobStatus, string> = {
  [ProviderImportJobStatus.Queued]: 'queued',
  [ProviderImportJobStatus.Running]: 'running',
  [ProviderImportJobStatus.Succeeded]: 'succeeded',
  [ProviderImportJobStatus.Failed]: 'failed',
};

/** Clave i18n del estado (`providerConsumptionImport.jobs.status.*`). */
export const importJobStatusLabelKey = (job: Pick<ProviderImportJob, 'status'>): string => {
  const status = importJobStatusOf(job);
  return `providerConsumptionImport.jobs.status.${status ? STATUS_KEYS[status] : 'unknown'}`;
};

/** Tono de la pastilla: en cola neutro, en curso info, terminado éxito, fallido error. */
export const importJobStatusTone = (job: Pick<ProviderImportJob, 'status'>): StatusTone => {
  switch (importJobStatusOf(job)) {
    case ProviderImportJobStatus.Running:
      return 'info';
    case ProviderImportJobStatus.Succeeded:
      return 'success';
    case ProviderImportJobStatus.Failed:
      return 'danger';
    default:
      return 'neutral';
  }
};

/** Progreso 0..100 (entero); undefined si no vino (barra indeterminada). Terminado bien = 100. */
export const importJobProgress = (job: Pick<ProviderImportJob, 'status' | 'progressPercent'> | null | undefined): number | undefined => {
  if (!job) return undefined;
  if (isImportJobSucceeded(job)) return 100;
  const value = Number(job.progressPercent);
  if (job.progressPercent === null || job.progressPercent === undefined || Number.isNaN(value)) return undefined;
  return Math.max(0, Math.min(100, Math.round(value)));
};

/** La respuesta de "Importar" es un job (202, MP 030) y no el resumen sincrónico anterior. */
export const isImportJob = (response: ImportStartResponse | unknown): response is ProviderImportJob => {
  const candidate = response as Partial<ProviderImportJob> | null | undefined;
  return !!candidate && typeof candidate === 'object' && !!candidate.id && candidate.status !== undefined && candidate.status !== null;
};

/** `jobId` del 409 "ya hay una importación en curso de este archivo"; undefined en cualquier otro error. */
export const conflictJobIdOf = (error: unknown): string | undefined => {
  const typed = error as Partial<ImportJobConflictError> | null | undefined;
  if (!typed || Number(typed.ErrorCode) !== 409 || !typed.JobId) return undefined;
  return String(typed.JobId);
};

/** Job provisorio mientras se lee el que ya estaba en curso (409 con jobId). */
export const placeholderImportJob = (jobId: string, providerId: number, providerFileId: string, fileName?: string): ProviderImportJob => ({
  id: jobId,
  providerId,
  providerFileId,
  fileName: fileName ?? null,
  status: ProviderImportJobStatus.Queued,
  stage: null,
  progressPercent: null,
});

/** Errores del job (mensaje + `errors[]`) sin repetir. */
export const importJobErrorText = (job: Pick<ProviderImportJob, 'errorMessage' | 'errors'>): string => {
  const parts = [job.errorMessage, ...(job.errors ?? [])].map((part) => String(part ?? '').trim()).filter(Boolean);
  return Array.from(new Set(parts)).join(' ');
};

/** Resumen del job con los contadores en 0 si faltan. */
export const importJobResultOf = (job: Pick<ProviderImportJob, 'result' | 'providerFileId'>): ImportProviderFileConsumptionsResult | undefined =>
  job.result ? { ...job.result, fileId: job.result.fileId ?? job.providerFileId, warnings: job.result.warnings ?? [] } : undefined;

/** Mismo proveedor (el job trae el id int; puede venir como string). */
export const isJobOfProvider = (job: Pick<ProviderImportJob, 'providerId'>, providerId: number): boolean => Number(job.providerId) === Number(providerId);

// ---------------------------------------------------------------------------------------------
// Vínculos del aviso
// ---------------------------------------------------------------------------------------------

/** "Ver consumos": grilla del proveedor filtrada por origen 5 y el archivo importado. */
export const importJobConsumptionsLink = (job: Pick<ProviderImportJob, 'providerId' | 'providerFileId'>): string =>
  `/providers/${encodeURIComponent(String(job.providerId))}?tab=consumptions&origins=${ProviderConsumptionOrigin.ProviderFileImport}&providerFileId=${encodeURIComponent(String(job.providerFileId))}`;

/** "Reintentar": abre el asistente de importación con el archivo elegido. */
export const importJobRetryLink = (job: Pick<ProviderImportJob, 'providerId' | 'providerFileId'>): string =>
  `/providers/${encodeURIComponent(String(job.providerId))}?tab=consumptions&importFileId=${encodeURIComponent(String(job.providerFileId))}`;

// ---------------------------------------------------------------------------------------------
// Fecha de corte de la consulta global
// ---------------------------------------------------------------------------------------------

/**
 * `activeOrSince` de la consulta global: el `requestedAt` (reloj del servidor) más viejo de los jobs activos
 * conocidos; si no hay, el inicio de la sesión. Los activos que igual no vengan se leen por id.
 */
export const importJobsPollSince = (jobs: ProviderImportJob[], sessionSince: string): string => {
  const times = jobs
    .filter(isImportJobActive)
    .map((job) => (job.requestedAt ? Date.parse(job.requestedAt) : NaN))
    .filter((time) => !Number.isNaN(time));
  if (times.length === 0) return sessionSince;
  const oldest = Math.min(...times);
  const session = Date.parse(sessionSince);
  return !Number.isNaN(session) && session < oldest ? sessionSince : new Date(oldest).toISOString();
};

// ---------------------------------------------------------------------------------------------
// Almacenamiento del navegador (siempre con try/catch)
// ---------------------------------------------------------------------------------------------

export const NOTIFIED_JOBS_STORAGE_KEY = 'prebiller.importJobs.notified';
export const SESSION_SINCE_STORAGE_KEY = 'prebiller.importJobs.since';
export const BROWSER_NOTIFY_STORAGE_KEY = 'prebiller.importJobs.browserNotify';
/** Cuántos ids avisados se recuerdan (los más nuevos). */
export const MAX_NOTIFIED_IDS = 200;

const session = (): Storage | undefined => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : undefined;
  } catch {
    return undefined;
  }
};

const local = (): Storage | undefined => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
};

/** Ids de jobs ya avisados en esta pestaña. */
export const loadNotifiedJobIds = (): string[] => {
  try {
    const raw = session()?.getItem(NOTIFIED_JOBS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

export const saveNotifiedJobIds = (ids: string[]): void => {
  try {
    session()?.setItem(NOTIFIED_JOBS_STORAGE_KEY, JSON.stringify(ids.slice(-MAX_NOTIFIED_IDS)));
  } catch {
    // sin almacenamiento: el aviso se puede repetir al recargar, nada más
  }
};

/** Inicio de la sesión de avisos de esta pestaña (se conserva al recargar). */
export const loadOrStartSessionSince = (now: Date = new Date()): string => {
  try {
    const stored = session()?.getItem(SESSION_SINCE_STORAGE_KEY);
    if (stored && !Number.isNaN(Date.parse(stored))) return stored;
    const value = now.toISOString();
    session()?.setItem(SESSION_SINCE_STORAGE_KEY, value);
    return value;
  } catch {
    return now.toISOString();
  }
};

/** El usuario pidió avisos del navegador (preferencia por navegador). */
export const getBrowserNotifyPreference = (): boolean => {
  try {
    return local()?.getItem(BROWSER_NOTIFY_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const setBrowserNotifyPreference = (value: boolean): void => {
  try {
    local()?.setItem(BROWSER_NOTIFY_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // preferencia sólo de esta sesión
  }
};

// ---------------------------------------------------------------------------------------------
// Notification API
// ---------------------------------------------------------------------------------------------

export type BrowserNotificationPermission = 'granted' | 'denied' | 'default' | 'unsupported';

const notificationApi = (): typeof Notification | undefined => {
  try {
    return typeof window !== 'undefined' && 'Notification' in window ? window.Notification : undefined;
  } catch {
    return undefined;
  }
};

export const browserNotificationPermission = (): BrowserNotificationPermission => {
  const api = notificationApi();
  if (!api) return 'unsupported';
  return (api.permission as BrowserNotificationPermission) ?? 'default';
};

/** Pide permiso una sola vez (sólo si todavía no se respondió). */
export const requestBrowserNotificationPermission = async (): Promise<BrowserNotificationPermission> => {
  const api = notificationApi();
  if (!api) return 'unsupported';
  if (api.permission !== 'default') return api.permission as BrowserNotificationPermission;
  try {
    return ((await api.requestPermission()) as BrowserNotificationPermission) ?? 'default';
  } catch {
    return 'default';
  }
};

/** La pestaña está oculta (minimizada o en otra pestaña). */
export const isDocumentHidden = (): boolean => {
  try {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  } catch {
    return false;
  }
};

/**
 * Notificación del navegador si la pestaña está oculta, hay permiso y el usuario la pidió. El click enfoca la
 * pestaña y ejecuta `onClick`. Devuelve si se mostró.
 */
export const showBrowserNotification = (title: string, body: string, tag: string, onClick?: () => void): boolean => {
  const api = notificationApi();
  if (!api || api.permission !== 'granted' || !getBrowserNotifyPreference() || !isDocumentHidden()) return false;
  try {
    const notification = new api(title, { body, tag });
    notification.onclick = () => {
      try {
        window.focus();
      } catch {
        // nada
      }
      onClick?.();
      notification.close();
    };
    return true;
  } catch {
    return false;
  }
};
